import { Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { useScanEquipmentPhoto } from '../../hooks/data/useEquipment';
import { useAppToast } from '../../hooks/ui/useAppToast';
import { useIsPhiWorkspace } from '../../hooks/user/useIsPhiWorkspace';
import { EntryOption } from '../features/create/EntryOption';
import { OrDivider } from '../features/create/OrDivider';
import { MaterialIcon } from '../ui/MaterialIcon';
import { DropZone } from '../upload/intake/DropZone';
import { IMAGE } from '../upload/intake/rules';
import { useFileIntake } from '../upload/intake/useFileIntake';
import type { AddEquipmentStep } from './addEquipmentForm';

/** What a nameplate scan read off the label, plus the photo it read. */
export interface EquipmentScan {
  readonly make: string;
  readonly model: string;
  readonly serial: string;
  readonly confidence: number;
  readonly file: File;
}

interface AddEquipmentOptionsStepProps {
  /** Hidden on a phone, where the camera is already to hand. */
  readonly showPhone: boolean;
  readonly onChoose: (step: Exclude<AddEquipmentStep, 'options'>) => void;
  /** A scan finished: `null` when it read nothing (the form opens empty). */
  readonly onScanned: (scan: EquipmentScan | null) => void;
}

/**
 * The largest label photo a scan can take: it travels base64-encoded (4/3 of
 * its size) in a JSON body, and the API's JSON limit is 10 MB (`app.ts`).
 */
const SCAN_PHOTO_MAX_BYTES = 7 * 1_048_576;

function optionIcon(name: string) {
  return <MaterialIcon name={name} boxSize="20px" color="text.secondary" flexShrink={0} mt="1px" />;
}

/**
 * Step 1 of Add equipment: a photo of the label first (the shared drop zone,
 * which on a phone offers the camera), then the other ways in.
 */
export function AddEquipmentOptionsStep({
  showPhone,
  onChoose,
  onScanned,
}: AddEquipmentOptionsStepProps) {
  const toast = useAppToast();
  // Restricted mode reaches no web: no instrument search, no manual lookup.
  const isPhiWorkspace = useIsPhiWorkspace();
  const scanPhoto = useScanEquipmentPhoto();
  const [isScanning, setIsScanning] = useState(false);

  const scanLabel = async (file: File) => {
    setIsScanning(true);
    try {
      const result = await scanPhoto.mutateAsync(await fileToBase64(file));
      if (!result.success || !result.data) {
        onScanned(null);
        return;
      }
      const { make, model, serial, confidence } = result.data;
      onScanned({ make, model, serial, confidence, file });
    } catch {
      toast({ title: 'Scan failed', description: 'Could not identify equipment', status: 'error' });
      onScanned(null);
    } finally {
      setIsScanning(false);
    }
  };

  const intake = useFileIntake({
    rule: IMAGE,
    surface: 'create_equipment',
    maxFiles: 1,
    maxBytes: SCAN_PHOTO_MAX_BYTES,
    readOnlyReason: isScanning ? 'Wait for this one to finish first.' : undefined,
    onFiles: ([file]) => void scanLabel(file),
  });

  return (
    <VStack spacing={3} align="stretch">
      {/* Mounted from the start, only its text changes: a live region that
          appears together with its text is not announced. Empty, it is 0px. */}
      <Text
        fontSize="sm"
        color="text.secondary"
        aria-live="polite"
        data-testid="equipment-scan-status"
        mb={isScanning ? 0 : -3}
      >
        {isScanning && 'Reading the label…'}
      </Text>
      <DropZone
        intake={intake}
        icon="document_scanner"
        title="Drop a photo of the label, or click to choose"
        touchTitle="Photograph the label"
        next={
          isPhiWorkspace
            ? 'Bower reads the make, model and serial. In Restricted mode it does not search the web for the manual.'
            : 'Bower reads the make, model and serial, then finds the manual.'
        }
      />
      <OrDivider />
      {showPhone && (
        <EntryOption
          icon={optionIcon('smartphone')}
          title="Photograph it with your phone"
          description="Shows a QR code to open Bower on your phone."
          onClick={() => onChoose('qr')}
        />
      )}
      {!isPhiWorkspace && (
        <EntryOption
          icon={optionIcon('travel_explore')}
          title="Search online"
          description="Enter the instrument name to look up specs and auto-fill details."
          onClick={() => onChoose('search')}
        />
      )}
      <EntryOption
        icon={optionIcon('edit')}
        title="Enter details by hand"
        description="Fill in the equipment details yourself."
        onClick={() => onChoose('form')}
      />
    </VStack>
  );
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
